import React, { type PropsWithoutRef, Suspense, JSX } from 'react';

import { getRemoteRuntime } from '@adsk/exo-skeleton-api';

import { RemoteLoaderWrapper } from './RemoteLoaderWrapper';
import { RemoteWrapper } from './RemoteWrapper';
import { useCreateLazyRemoteComponent } from '../hooks/useCreateLazyRemoteComponent';
import { useRemoteTrackingHandler } from '../hooks/useRemoteTrackingHandler';
import { RemoteComponentProps } from '../types';

export type { RemoteComponentProps } from '../types';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
function LazyRemoteComponent<PROPS extends {} = {}>({
  moduleName,
  scope,
  renderLoader = () => null,
  version,
  importTimeoutMs,
  ...props
}: RemoteComponentProps<PROPS>): JSX.Element {
  const { moniker } = getRemoteRuntime(scope) || {};

  useRemoteTrackingHandler({ scope, moduleName });
  const Component = useCreateLazyRemoteComponent<PROPS>({
    scope,
    moduleName,
    version,
    timeoutMs: importTimeoutMs,
  });

  const componentContent = (
    <Component {...(props as unknown as PropsWithoutRef<PROPS>)} />
  );

  return renderLoader ? (
    <Suspense
      fallback={
        <RemoteLoaderWrapper scope={scope} moduleName={moduleName}>
          {renderLoader({ scope, moduleName, moniker })}
        </RemoteLoaderWrapper>
      }
    >
      {componentContent}
    </Suspense>
  ) : (
    componentContent
  );
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const RemoteComponent = <PROPS extends {} = {}>({
  children,
  ...props
}: RemoteComponentProps<PROPS>) => (
  <RemoteWrapper {...props}>
    <LazyRemoteComponent {...props}>{children}</LazyRemoteComponent>
  </RemoteWrapper>
);
